import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh, faUserMd, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useAtendimentos } from '../hooks/useAtendimentos';
import { useAuth } from '../hooks/useAuth';
import { useRoleSelection } from '../hooks/useRoleSelection';
import PageHeader from '../components/PageHeader';
import Button from '../components/Button';
import PatientQueueCard from '../components/PatientQueueCard';
import Alert from '../components/Alert';

const AtendimentosConsultorio: React.FC = () => {
  const { currentOrganization } = useAuth();
  const { clearRoleSelection } = useRoleSelection();
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const organizationId = currentOrganization?.id || '';

  const { atendimentos, loading, error, callPatient, finishAttendance, refetch } =
    useAtendimentos(organizationId);

  const handleCallPatient = async (atendimentoId: string) => {
    setActionLoading(atendimentoId);
    try {
      await callPatient(atendimentoId);
    } finally {
      setActionLoading(null);
    }
  };

  const handleFinishAttendance = async (atendimentoId: string) => {
    setActionLoading(atendimentoId);
    try {
      await finishAttendance(atendimentoId);
    } finally {
      setActionLoading(null);
    }
  };

  const waitingPatients = atendimentos.filter(
    a => !a.called_at && !a.finished_at && !a.canceled_at
  );
  const calledPatients = atendimentos.filter(a => a.called_at && !a.finished_at && !a.canceled_at);
  const nextPatient = waitingPatients[0];

  return (
    <main className="w-full flex-grow p-6">
      <div className="mx-auto space-y-6">
        <div className="flex justify-between items-start">
          <PageHeader title="Consultório Médico" subtitle="Gerencie o atendimento dos pacientes" />
          <Button
            variant="outline"
            leftIcon={faArrowLeft}
            onClick={clearRoleSelection}
            disabled={!!actionLoading}
            size="sm"
          >
            Trocar Papel
          </Button>
        </div>

        <div className="flex justify-between items-center">
          <Button
            variant="secondary"
            leftIcon={faRefresh}
            onClick={refetch}
            loading={loading}
            disabled={!!actionLoading}
          >
            Atualizar
          </Button>

          <div className="text-sm text-gray-600">
            <span className="font-medium">{waitingPatients.length}</span> aguardando •
            <span className="font-medium text-blue-600 ml-1">{calledPatients.length}</span> em
            atendimento
          </div>
        </div>

        {error && <Alert variant="error" title="Erro ao carregar fila" message={error} />}

        {calledPatients.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-blue-600 mb-4 flex items-center">
              <FontAwesomeIcon icon={faUserMd} className="mr-2" />
              Pacientes em Atendimento
            </h3>
            <div className="grid gap-4">
              {calledPatients.map(atendimento => (
                <PatientQueueCard
                  key={atendimento.id}
                  atendimento={atendimento}
                  onFinish={handleFinishAttendance}
                  showActions={{ call: false, finish: true, cancel: false }}
                  loading={actionLoading}
                />
              ))}
            </div>
          </div>
        )}

        {nextPatient && (
          <div>
            <h3 className="text-lg font-semibold text-green-600 mb-4">Próximo Paciente</h3>
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-1">
              <PatientQueueCard
                atendimento={nextPatient}
                onCall={handleCallPatient}
                showActions={{ call: true, finish: false, cancel: false }}
                loading={actionLoading}
              />
            </div>
          </div>
        )}

        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Fila de Espera ({Math.max(0, waitingPatients.length - 1)} após o próximo)
          </h3>

          {loading && !atendimentos.length ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Carregando fila de atendimento...</p>
            </div>
          ) : waitingPatients.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <FontAwesomeIcon icon={faUserMd} className="w-12 h-12 text-gray-400 mb-4" />
              <p className="text-gray-600">Nenhum paciente na fila de espera</p>
            </div>
          ) : waitingPatients.length === 1 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-600">Apenas o próximo paciente está na fila</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {waitingPatients.slice(1).map((atendimento, index) => (
                <div key={atendimento.id} className="relative opacity-75">
                  <div className="absolute -left-4 top-4 bg-gray-400 text-white text-xs font-bold rounded-full w-8 h-8 flex items-center justify-center">
                    {index + 2}
                  </div>
                  <PatientQueueCard
                    atendimento={atendimento}
                    showActions={{ call: false, finish: false, cancel: false }}
                    loading={actionLoading}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default AtendimentosConsultorio;
